<script>
import { mapGetters } from 'vuex';
import { getSidebarItems } from './config/default-sidebar';
import { AVAILABLE_MODULES } from '../../routes/dashboard/settings/module/constants/modules';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useRoute, useRouter } from 'dashboard/composables/route';

import PrimarySidebar from './sidebarComponents/Primary.vue';
import SecondarySidebar from './sidebarComponents/Secondary.vue';
import { routesWithPermissions } from '../../routes';
import {
  getUserPermissions,
  hasPermissions,
} from '../../helper/permissionsHelper';
import {
  canAccessMenuItem,
  getAllowedMenuItems,
} from '../../helper/menuPermissionsHelper';
import {
  hasOrganizationAccess,
  isOrganizationAccount,
} from '../../helper/organizationAccessHelper';
import { hasHighTierAccess } from '../../helper/subscriptionHelper';

export default {
  components: {
    PrimarySidebar,
    SecondarySidebar,
  },
  props: {
    showSecondarySidebar: {
      type: Boolean,
      default: true,
    },
    sidebarClassName: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const route = useRoute();
    const router = useRouter();

    const toggleKeyShortcutModal = () => {
      emit('openKeyShortcutModal');
    };
    const closeKeyShortcutModal = () => {
      emit('closeKeyShortcutModal');
    };
    const isCurrentRouteSameAsNavigation = routeName => {
      return route.name === routeName;
    };
    const navigateToRoute = routeName => {
      if (!isCurrentRouteSameAsNavigation(routeName)) {
        router.push({ name: routeName });
      }
    };
    const keyboardEvents = {
      '$mod+Slash': {
        action: toggleKeyShortcutModal,
      },
      '$mod+Escape': {
        action: closeKeyShortcutModal,
      },
      'Alt+KeyC': {
        action: () => navigateToRoute('home'),
      },
      'Alt+KeyV': {
        action: () => navigateToRoute('contacts_dashboard'),
      },
      'Alt+KeyR': {
        action: () => navigateToRoute('account_overview_reports'),
      },
      'Alt+KeyS': {
        action: () => navigateToRoute('agent_list'),
      },
    };
    useKeyboardEvents(keyboardEvents);

    return {
      toggleKeyShortcutModal,
    };
  },
  data() {
    return {
      showOptionsMenu: false,
    };
  },

  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      inboxes: 'inboxes/getInboxes',
      isACustomBrandedInstance: 'globalConfig/isACustomBrandedInstance',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
      labels: 'labels/getLabelsOnSidebar',
      teams: 'teams/getMyTeams',
      moduleId: 'stripe/getModuleId',
    }),
    activeCustomView() {
      if (this.activePrimaryMenu?.key === 'contacts') {
        return 'contact';
      }
      if (this.activePrimaryMenu?.key === 'conversations') {
        return 'conversation';
      }
      return '';
    },
    customViews() {
      return this.$store.getters['customViews/getCustomViewsByFilterType'](
        this.activeCustomView
      );
    },
    isConversationOrContactActive() {
      return (
        this.activePrimaryMenu?.key === 'contacts' ||
        this.activePrimaryMenu?.key === 'conversations'
      );
    },
    sideMenuConfig() {
      return getSidebarItems(this.accountId);
    },
    primaryMenuItems() {
      const userPermissions = getUserPermissions(
        this.currentUser,
        this.accountId
      );
      const menuItems = this.sideMenuConfig.primaryMenu;

      // Determine if granular menu control is enabled
      // Uses helper that handles precedence: agent > team > null (role-based)
      const allowedMenuItems = getAllowedMenuItems(
        this.currentUser,
        this.accountId,
        this.teams
      );
      const useGranularControl = allowedMenuItems !== null;

      const filteredItems = menuItems.filter(menuItem => {
        // If granular control is enabled (allowed_menu_items is not null),
        // use allowed_menu_items to determine access. Otherwise, use role-based permissions.

        if (useGranularControl) {
          // Granular control mode: check if menu key is in allowed_menu_items
          const hasMenuAccess = canAccessMenuItem(
            menuItem.key,
            this.currentUser,
            this.accountId,
            this.teams
          );

          if (!hasMenuAccess) {
            return false;
          }
        } else {
          // Normal role-based mode: check role permissions
          const isAvailableForTheUser = hasPermissions(
            routesWithPermissions[menuItem.toStateName],
            userPermissions
          );

          if (!isAvailableForTheUser) {
            return false;
          }
        }

        // 2. Check chatwoot instances (existing)
        if (
          menuItem.alwaysVisibleOnChatwootInstances &&
          !this.isACustomBrandedInstance
        ) {
          return true;
        }

        // 3. Check account-level feature flags (existing + enhanced)
        if (menuItem.featureFlag) {
          if (menuItem.featureFlag === 'subscription_settings') {
            return true;
          }
          if (menuItem.featureFlag === 'promo_code') {
            return true;
          }
          const featureEnabled = this.isFeatureEnabledonAccount(
            this.accountId,
            menuItem.featureFlag
          );
          if (!featureEnabled) {
            return false;
          }
        }

        // 4. Temporary disabled
        if (
          menuItem.key === 'rental_branches' ||
          menuItem.key === 'rental_bookings'
        ) {
          return false;
        }

        // 5. Organization vs legacy branches: hasOrganizationAccess is the
        // single source of truth shared with the route guards and onboarding
        // task versioning (see helper/organizationAccessHelper.js).
        if (menuItem.key === 'organization') {
          if (!hasOrganizationAccess(this.$store, this.accountId.toString())) {
            return false;
          }
        } else if (menuItem.key === 'branches') {
          if (isOrganizationAccount(this.$store, this.accountId.toString())) {
            return false;
          }
        }

        // 5b. Account-specific restrictions (include/exclude), for any other
        // menu item that still uses the generic restriction config
        if (menuItem.accountRestriction) {
          const accountIdStr = this.accountId.toString();

          // Check exclude list
          if (menuItem.accountRestriction.exclude) {
            if (menuItem.accountRestriction.exclude.includes(accountIdStr)) {
              return false;
            }
          }

          // Check include list (if specified, only these accounts can see it)
          if (menuItem.accountRestriction.include) {
            if (!menuItem.accountRestriction.include.includes(accountIdStr)) {
              return false;
            }
          }
        }

        // 6. Module-based restrictions
        const activeModule = AVAILABLE_MODULES.find(m => m.id === this.moduleId);
        if (activeModule?.disabledMenuItems?.includes(menuItem.key)) {
          return false;
        }
        if (menuItem.moduleRequired && this.moduleId !== menuItem.moduleRequired) {
          return false;
        }

        // 7. Mantap roadshow
        if (menuItem.key === 'mantap' && this.accountId.toString() !== '203') {
          return false;
        }

        // 8. Tier-restricted items (enterprise/pro only)
        if (menuItem.tierRestricted && !hasHighTierAccess(this.$store)) {
          return false;
        }

        // 8. Module-marketplace restricted items — only visible while this
        // specific module is the account's active subscribed module (modules
        // are mutually exclusive, so this also hides it when another module
        // is active).
        if (menuItem.moduleRestricted) {
          const moduleName = this.$store.getters['stripe/getModuleName'];
          if (
            !hasHighTierAccess(this.$store) ||
            moduleName !== menuItem.moduleRestricted
          ) {
            return false;
          }
        }

        return true;
      });

      if (this.$store.getters['onboarding/remainingLocalCount'] > 0) {
        const onboardingIndex = filteredItems.findIndex(
          item => item.key === 'onboarding'
        );

        if (onboardingIndex > -1) {
          const [onboardingItem] = filteredItems.splice(onboardingIndex, 1);
          filteredItems.unshift(onboardingItem);
        }
      }

      return filteredItems.filter(item => item.key !== 'onboarding');
    },
    activeSecondaryMenu() {
      const { secondaryMenu } = this.sideMenuConfig;
      const { name: currentRoute } = this.$route;

      const activeSecondaryMenu =
        secondaryMenu.find(menuItem =>
          menuItem.routes.includes(currentRoute)
        ) || {};
      return activeSecondaryMenu;
    },
    activePrimaryMenu() {
      const activePrimaryMenu =
        this.primaryMenuItems.find(
          menuItem => menuItem.key === this.activeSecondaryMenu.parentNav
        ) || {};
      return activePrimaryMenu;
    },
    hasValidSecondaryMenu() {
      // Force show secondary menu for conversations
      if (this.activeSecondaryMenu.parentNav === 'conversations') {
        return true;
      }
      return Object.keys(this.activeSecondaryMenu).length > 0;
    },
  },

  watch: {
    activeCustomView() {
      this.fetchCustomViews();
    },
  },
  mounted() {
    this.$store.dispatch('labels/get');
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('notifications/unReadCount');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('attributes/get');
    this.fetchCustomViews();
  },

  methods: {
    fetchCustomViews() {
      if (this.isConversationOrContactActive) {
        this.$store.dispatch('customViews/get', this.activeCustomView);
      }
    },
    toggleSupportChatWindow() {
      window.$chatwoot.toggle();
    },
    toggleAccountModal() {
      this.$emit('toggleAccountModal');
    },
    showAddLabelPopup() {
      this.$emit('showAddLabelPopup');
    },
    openNotificationPanel() {
      this.$emit('openNotificationPanel');
    },
  },
};
</script>

<template>
  <aside class="flex h-full overflow-y-auto">
    <PrimarySidebar
      :logo-source="globalConfig.logoThumbnail"
      :installation-name="globalConfig.installationName"
      :is-a-custom-branded-instance="isACustomBrandedInstance"
      :account-id="accountId"
      :menu-items="primaryMenuItems"
      :active-menu-item="activePrimaryMenu.key"
      @toggleAccounts="toggleAccountModal"
      @openKeyShortcutModal="toggleKeyShortcutModal"
      @openNotificationPanel="openNotificationPanel"
    />
    <SecondarySidebar
      v-if="showSecondarySidebar && hasValidSecondaryMenu"
      :class="sidebarClassName"
      :account-id="accountId"
      :inboxes="inboxes"
      :labels="labels"
      :teams="teams"
      :custom-views="customViews"
      :menu-config="activeSecondaryMenu"
      :current-user="currentUser"
      :is-on-chatwoot-cloud="isOnChatwootCloud"
      @addLabel="showAddLabelPopup"
      @toggleAccounts="toggleAccountModal"
    />
  </aside>
</template>

